// ============================================================================
// Clinical text snippets — curated shortcuts for routine clinical
// documentation. Surfaced via the SnippetPicker component (the 💡 button
// next to any long-form field in the encounter editor).
//
// Each snippet has:
//   - trigger     short canonical name shown in the picker
//   - aliases     extra search terms (so "triple antibiotics" and
//                 "triple therapy" both find the same regimen)
//   - category    grouping label — drives popover headings
//   - sections    CNE section letters (B, D, G, L, N, P, R) where this
//                 snippet is relevant. The picker filters by section
//                 so the doctor never sees a treatment regimen offered
//                 under HPI, or a history paragraph offered under
//                 Empirical treatment. See web/src/features/doctor/
//                 CneSidebar.jsx for the canonical section letters.
//   - body        the text that gets inserted into the field
//
// PNG-aware defaults — doses + drugs reflect what's commonly available
// in PNG hospitals + what the National Treatment Protocols recommend.
// Edit / extend freely; this is a flat array so new entries land
// without any schema work — just remember to set `sections` so the
// snippet shows up in the right popover.
// ============================================================================

export const CLINICAL_SNIPPETS = [
  // ====================================================================
  // ANTIBIOTIC / ANTIMICROBIAL REGIMENS
  // Relevant to L (Empirical treatment — started pre-cultures) and
  // N (Treatment plan — definitive once dx confirmed).
  // ====================================================================
  {
    trigger: 'Triple antibiotics (IV)',
    aliases: ['triple therapy', 'sepsis cover', 'broad spectrum iv'],
    category: 'Antibiotics',
    sections: ['L', 'N'],
    body:
      'Ceftriaxone 1 g IV stat., ' +
      'Metronidazole (Flagyl) 500 mg IV stat., ' +
      'Gentamicin 160 mg IV stat.',
  },
  {
    trigger: 'Sepsis (severe) — empirical',
    aliases: ['septic shock', 'severe sepsis'],
    category: 'Antibiotics',
    sections: ['L', 'N'],
    body:
      'Ceftriaxone 2 g IV stat., then 1 g IV BD. ' +
      'Metronidazole 500 mg IV TDS. ' +
      'Add Gentamicin 5 mg/kg IV daily if shock or source-unknown. ' +
      'Source control + IV fluids + lactate monitoring.',
  },
  {
    trigger: 'Pneumonia — community acquired',
    aliases: ['cap', 'pneumonia oral'],
    category: 'Antibiotics',
    sections: ['L', 'N'],
    body:
      'Amoxicillin 1 g PO TDS for 5 days. ' +
      'If severe / hospitalised: Ceftriaxone 1 g IV daily + Azithromycin 500 mg PO daily. ' +
      'Oxygen to maintain SpO₂ ≥ 92%. ' +
      'Review at 48 hours.',
  },
  {
    trigger: 'UTI — uncomplicated',
    aliases: ['cystitis'],
    category: 'Antibiotics',
    sections: ['L', 'N'],
    body:
      'Trimethoprim 300 mg PO daily for 3 days, OR ' +
      'Nitrofurantoin 100 mg PO QID for 5 days. ' +
      'Fluids + symptom relief. Return if fever / flank pain.',
  },
  {
    trigger: 'Pyelonephritis',
    aliases: ['kidney infection', 'upper uti'],
    category: 'Antibiotics',
    sections: ['L', 'N'],
    body:
      'Ceftriaxone 1 g IV daily for 7–10 days, step down to oral on clinical improvement. ' +
      'IV fluids, paracetamol PRN. ' +
      'Urine MC&S sent. Review at 48 hours.',
  },
  {
    trigger: 'Ceftriaxone empiric cover',
    aliases: ['ceftriaxone', 'empiric monotherapy', 'broad spectrum'],
    category: 'Antibiotics',
    sections: ['L'],
    body:
      'Ceftriaxone 1 g IV daily for empirical Gram-negative + Gram-positive cover. ' +
      'Suitable while awaiting culture results. ' +
      'Adjust per sensitivities once available. ' +
      'Continue for ___ days, or step down to oral once afebrile and clinically improved.',
  },
  {
    trigger: 'Oral antibiotics — first line',
    aliases: ['oral antibiotics', 'po antibiotics', 'amoxicillin first line'],
    category: 'Antibiotics',
    sections: ['L'],
    body:
      'First-line oral antibiotic per clinical picture: ' +
      'Amoxicillin 500 mg – 1 g PO TDS for 5 days (7 if severe). ' +
      'Doxycycline 100 mg PO BD as alternative if penicillin-allergic. ' +
      'Review at 48 hours; escalate to IV if not improving.',
  },

  // ====================================================================
  // TB / MALARIA / TROPICAL — PNG endemic conditions
  // Relevant to L and N.
  // ====================================================================
  {
    trigger: 'TB — quadruple therapy (intensive phase)',
    aliases: ['rhze', 'anti tb', 'tb treatment'],
    category: 'TB & Tropical',
    sections: ['L', 'N'],
    body:
      'RHZE (rifampicin / isoniazid / pyrazinamide / ethambutol) — fixed-dose combination, ' +
      'weight-based daily dosing for 2 months intensive phase. ' +
      'Then RH (rifampicin / isoniazid) for 4 months continuation. ' +
      'DOTS supervision. Liver function + visual acuity baseline.',
  },
  {
    trigger: 'Severe malaria — adult',
    aliases: ['severe malaria', 'cerebral malaria'],
    category: 'TB & Tropical',
    sections: ['L', 'N'],
    body:
      'Artesunate 2.4 mg/kg IV at 0, 12 and 24 hours, then daily until oral therapy tolerated. ' +
      'Step down to full course of artemether-lumefantrine (Coartem) for 3 days. ' +
      'Glucose monitoring + treat hypoglycaemia. ' +
      'IV fluids cautious — pulmonary oedema risk. ' +
      'Treat any co-existing bacterial sepsis.',
  },
  {
    trigger: 'Uncomplicated malaria',
    aliases: ['simple malaria', 'coartem'],
    category: 'TB & Tropical',
    sections: ['L', 'N'],
    body:
      'Artemether-lumefantrine (Coartem) — weight-based dosing for 3 days. ' +
      'Take with fatty meal. ' +
      'Paracetamol for fever. ' +
      'Return immediately if vomiting / drowsiness / unable to drink.',
  },
  {
    trigger: 'Dengue — supportive',
    aliases: ['dengue fever'],
    category: 'TB & Tropical',
    sections: ['L', 'N'],
    body:
      'Bed rest. Oral fluids — ORS preferred. Paracetamol for fever. ' +
      'AVOID NSAIDs and aspirin. ' +
      'Daily FBC (platelet + haematocrit). ' +
      'Warning signs: persistent vomiting, mucosal bleeding, lethargy, abdominal pain — ' +
      'admit for IV fluids if any present.',
  },
  {
    trigger: 'Suspected TB — pathway',
    aliases: ['suspected tb', 'tb workup', 'pulmonary tb workup'],
    category: 'TB & Tropical',
    sections: ['L'],
    body:
      'Suspected pulmonary TB. Isolate / advise mask + cough hygiene. ' +
      'Send sputum × 3 for AFB smear + GeneXpert. CXR. HIV test. ' +
      'Refer / notify TB programme. ' +
      'Defer empirical RHZE until microbiological confirmation UNLESS severely unwell and clinical / radiological diagnosis strong. ' +
      'Counsel patient on isolation and adherence implications.',
  },

  // ====================================================================
  // CHRONIC DISEASE STARTS
  // Relevant to N (definitive treatment plan), not L (these aren't
  // empirical — diagnosis is usually established before starting).
  // ====================================================================
  {
    trigger: 'Hypertension — newly diagnosed',
    aliases: ['htn start', 'new hypertension'],
    category: 'Chronic disease',
    sections: ['N'],
    body:
      'Lifestyle counselling: salt restriction, weight loss, exercise 30 min ≥ 5 days/week, ' +
      'limit alcohol, smoking cessation. ' +
      'Start Amlodipine 5 mg PO daily; titrate to 10 mg if BP not at target in 4 weeks. ' +
      'Add HCTZ 12.5 mg daily if dual therapy needed. ' +
      'Baseline ECG + UEC + lipids + urine ACR. ' +
      'Review BP in 2 weeks.',
  },
  {
    trigger: 'Type 2 diabetes — newly diagnosed',
    aliases: ['t2dm start', 'new diabetes'],
    category: 'Chronic disease',
    sections: ['N'],
    body:
      'Lifestyle: low-GI diet, weight loss target 5–10%, exercise 30 min ≥ 5 days/week. ' +
      'Start Metformin 500 mg PO BD with meals; titrate to 1 g BD over 2–4 weeks. ' +
      'Baseline HbA1c + UEC + lipids + urine ACR + retinal screen + foot check. ' +
      'BP target < 130/80. Statin if eligible. ' +
      'Diabetes nurse referral. Review HbA1c in 3 months.',
  },
  {
    trigger: 'Asthma — exacerbation (moderate)',
    aliases: ['asthma flare', 'asthma attack'],
    category: 'Chronic disease',
    sections: ['L', 'N'],
    body:
      'Salbutamol 5 mg neb (or 6–8 puffs spacer) every 20 min for 1 hour, then PRN. ' +
      'Ipratropium 500 mcg neb if severe. ' +
      'Prednisolone 40–50 mg PO stat, then daily for 5 days. ' +
      'Oxygen to maintain SpO₂ ≥ 94%. ' +
      'Re-check PEF + auscultation post-bronchodilator. ' +
      'Asthma action plan reviewed.',
  },

  // ====================================================================
  // FLUIDS / SUPPORTIVE CARE
  // Relevant to L (resus before dx) and N (maintenance with plan).
  // ====================================================================
  {
    trigger: 'IV fluid — adult resuscitation',
    aliases: ['fluid bolus', 'resus fluids'],
    category: 'Fluids & supportive',
    sections: ['L', 'N'],
    body:
      'Hartmann\'s (Ringer\'s lactate) 500 mL IV over 15 min. ' +
      'Reassess HR, BP, capillary refill, urine output. ' +
      'Repeat once if hypotensive; total ≤ 30 mL/kg before reassessing for cardiac / pulmonary status.',
  },
  {
    trigger: 'IV fluid — adult maintenance',
    aliases: ['maintenance fluids'],
    category: 'Fluids & supportive',
    sections: ['L', 'N'],
    body:
      '0.9% Normal Saline OR Hartmann\'s 1 L IV over 8 hours. ' +
      'Add KCl 20 mmol/L if eating poorly and K⁺ < 4. ' +
      'Daily UEC if continued > 24 hours.',
  },
  {
    trigger: 'ORS protocol — adult',
    aliases: ['oral rehydration', 'gastro fluids'],
    category: 'Fluids & supportive',
    sections: ['L', 'N'],
    body:
      'Oral Rehydration Solution (ORS): 200 mL after each loose stool, ' +
      'plus continued normal fluid intake. ' +
      'Zinc 20 mg PO daily for 10 days. ' +
      'Paracetamol PRN. Avoid antimotility agents. ' +
      'Return if signs of dehydration, fever > 39°C, or blood in stool.',
  },
  {
    trigger: 'Pain relief / antiemetic',
    aliases: ['analgesia', 'pain control', 'antiemetic'],
    category: 'Fluids & supportive',
    sections: ['L'],
    body:
      'Paracetamol 1 g PO QID regularly. ' +
      'Tramadol 50–100 mg PO QID PRN OR IV morphine 2.5–5 mg PRN for severe pain ' +
      '(titrate, monitor sedation + respiratory rate). ' +
      'Metoclopramide 10 mg PO / IV TDS PRN for nausea. ' +
      'Avoid NSAIDs if dehydrated, renal impairment, or GI bleed risk.',
  },

  // ====================================================================
  // EXAMINATION TEMPLATES
  // Relevant to G only.
  // ====================================================================
  {
    trigger: 'Normal general examination',
    aliases: ['normal exam', 'unremarkable exam'],
    category: 'Examination',
    sections: ['G'],
    body:
      'Alert and oriented to time, place, person. No respiratory distress. Well-perfused, no pallor / jaundice / cyanosis / oedema. ' +
      'Cardiovascular: HS I + II, no murmurs. ' +
      'Respiratory: equal air entry bilaterally, vesicular breath sounds, no added sounds. ' +
      'Abdomen: soft, non-tender, no organomegaly, bowel sounds present. ' +
      'Neurological: GCS 15, CN II–XII intact, normal tone / power / reflexes / sensation.',
  },
  {
    trigger: 'Respiratory examination',
    aliases: ['resp exam', 'chest exam'],
    category: 'Examination',
    sections: ['G'],
    body:
      'RS: respiratory rate ___/min, SpO₂ ___% on room air. ' +
      'No respiratory distress, no use of accessory muscles. ' +
      'Trachea central. Chest expansion symmetrical. ' +
      'Percussion resonant throughout. ' +
      'Auscultation: equal vesicular breath sounds bilaterally; ' +
      'no crepitations, wheeze, or bronchial breathing. ' +
      'Vocal resonance normal.',
  },
  {
    trigger: 'Cardiovascular examination',
    aliases: ['cvs exam', 'cardiac exam'],
    category: 'Examination',
    sections: ['G'],
    body:
      'CVS: pulse ___ /min regular, BP ___ /___ mmHg. ' +
      'JVP not raised. ' +
      'Apex beat undisplaced, 5th ICS in mid-clavicular line. ' +
      'Heart sounds I + II normal, no added sounds or murmurs. ' +
      'No carotid bruits. ' +
      'Peripheries warm, no pedal oedema. ' +
      'Capillary refill < 2 seconds.',
  },
  {
    trigger: 'Abdominal examination',
    aliases: ['abdo exam', 'abdomen exam'],
    category: 'Examination',
    sections: ['G'],
    body:
      'Abdomen: soft, ___ (tender / non-tender) in ___ region. ' +
      'No rebound, no guarding. ' +
      'No palpable masses. ' +
      'Liver, spleen and kidneys not enlarged. ' +
      'Bowel sounds present and normoactive. ' +
      'Hernial orifices clear. ' +
      'PR / urine examination as indicated.',
  },
  {
    trigger: 'Neurological examination',
    aliases: ['neuro exam', 'cns exam'],
    category: 'Examination',
    sections: ['G'],
    body:
      'CNS: GCS 15 (E4 V5 M6). Oriented to time, place, person. ' +
      'Cranial nerves II–XII grossly intact. ' +
      'Tone normal in all four limbs. Power 5/5 throughout. ' +
      'Reflexes ++ symmetrically. Plantars flexor. ' +
      'Sensation to light touch and pinprick intact. ' +
      'Coordination normal. Gait normal (if assessable).',
  },

  // ====================================================================
  // HISTORY TEMPLATES (HPI)
  // Relevant to B only — these are presenting-complaint narratives,
  // NOT treatment.
  // ====================================================================
  {
    trigger: 'URTI — typical history',
    aliases: ['cold history', 'cough cold'],
    category: 'History templates',
    sections: ['B'],
    body:
      '3-day history of sore throat, blocked nose, dry cough, ' +
      'low-grade fever (~37.8 °C), mild generalised body aches. ' +
      'No dyspnoea, no chest pain, no haemoptysis. ' +
      'No sick contacts. Eating + drinking normally. Activity reduced.',
  },
  {
    trigger: 'Gastroenteritis — typical history',
    aliases: ['gastro history', 'diarrhoea history'],
    category: 'History templates',
    sections: ['B'],
    body:
      '24-hour history of profuse watery diarrhoea (5–6 episodes), ' +
      'associated nausea + non-bilious vomiting. ' +
      'Mild crampy abdominal pain, no blood / mucus in stool. ' +
      'No fever recorded. Reduced oral intake. ' +
      'No recent antibiotic use. No sick contacts.',
  },
  {
    trigger: 'Chest pain history',
    aliases: ['chest pain', 'cardiac history', 'acs history'],
    category: 'History templates',
    sections: ['B'],
    body:
      '___ hour history of central / left-sided chest pain. ' +
      'Character: tight / heavy / sharp / burning. Radiation: arm / jaw / back / none. ' +
      'Duration of current episode: ___ minutes. ' +
      'Aggravated by exertion; relieved by rest / GTN. ' +
      'Associated SOB, sweating, nausea. No haemoptysis. ' +
      'Cardiac risk factors: HTN, DM, smoking, family history of CAD.',
  },
  {
    trigger: 'Abdominal pain history',
    aliases: ['abdo pain', 'abdominal pain'],
    category: 'History templates',
    sections: ['B'],
    body:
      'Onset ___ hours/days ago. Location: epigastric / RUQ / LUQ / RLQ / LLQ / generalised. ' +
      'Character: crampy / colicky / constant / sharp. ' +
      'Radiation: back / shoulder / groin / none. Severity ___/10. ' +
      'Aggravated by food / movement; relieved by ___. ' +
      'Associated: nausea, vomiting, diarrhoea, fever, urinary symptoms. ' +
      'Last bowel motion: ___. Last menstrual period (if applicable): ___.',
  },
  {
    trigger: 'Weight loss / poor appetite',
    aliases: ['weight loss history', 'cachexia', 'anorexia history'],
    category: 'History templates',
    sections: ['B'],
    body:
      '___ kg weight loss over the past ___ months/weeks. Reduced appetite. ' +
      'No deliberate dieting. ' +
      'Associated fatigue, night sweats, fevers. ' +
      'No change in bowel habit / urinary symptoms / cough / haemoptysis. ' +
      'PMHx: ___. No similar in family. Patient considers this a significant change.',
  },
  {
    trigger: 'TB symptoms history',
    aliases: ['tb history', 'pulmonary tb', 'cough chronic'],
    category: 'History templates',
    sections: ['B'],
    body:
      'Chronic cough > 2 weeks, productive of mucoid / yellow sputum. ' +
      'Haemoptysis: ___. ' +
      'Night sweats and weight loss ___ kg over past ___ months. ' +
      'Low-grade evening fever. Reduced appetite. ' +
      'Contact history: lives with / cares for known TB case (yes / no). ' +
      'HIV status: ___. Previous TB treatment: ___. BCG scar present.',
  },

  // ====================================================================
  // REVIEW OF SYSTEMS — NEW (added 2026-06-04)
  // Relevant to D only. Compact system-by-system templates the
  // clinician can edit/extend.
  // ====================================================================
  {
    trigger: 'ROS — all systems negative',
    aliases: ['ros normal', 'systems review nil', 'wnl ros'],
    category: 'Review of systems',
    sections: ['D'],
    body:
      'General: no fever, weight loss, night sweats, fatigue. ' +
      'CVS: no chest pain, palpitations, orthopnoea, ankle swelling. ' +
      'Resp: no cough, sputum, haemoptysis, dyspnoea, wheeze. ' +
      'GI: appetite normal, no nausea / vomiting / abdominal pain, bowels regular. ' +
      'GU: no dysuria, frequency, haematuria. Menses (if applicable): regular. ' +
      'MSK: no joint pain, stiffness, swelling. ' +
      'Neuro: no headache, dizziness, visual disturbance, weakness, numbness. ' +
      'Skin: no rash, itching, new lesions. ' +
      'Endocrine: no polyuria, polydipsia, heat / cold intolerance.',
  },
  {
    trigger: 'ROS — respiratory positive',
    aliases: ['ros resp', 'cough ros'],
    category: 'Review of systems',
    sections: ['D'],
    body:
      'General: low-grade fever, fatigue +. No weight loss / night sweats. ' +
      'Resp: cough productive of yellow sputum, mild dyspnoea on exertion, no haemoptysis, no chest pain. ' +
      'CVS: no chest pain, palpitations, oedema. ' +
      'GI: appetite reduced, no nausea / vomiting. ' +
      'GU: no urinary symptoms. ' +
      'MSK / Neuro / Skin / Endocrine: nil of note.',
  },
  {
    trigger: 'ROS — GI positive',
    aliases: ['ros gi', 'abdominal ros'],
    category: 'Review of systems',
    sections: ['D'],
    body:
      'General: nil constitutional symptoms. ' +
      'GI: epigastric / abdominal pain as per HPI. Nausea +, vomiting +/-. ' +
      'Appetite reduced. Bowels: as documented. No haematemesis, no melaena, no PR bleeding. ' +
      'CVS: no chest pain / palpitations. Resp: no cough / dyspnoea. ' +
      'GU: no dysuria. MSK / Neuro / Skin / Endocrine: nil of note.',
  },
  {
    trigger: 'ROS — cardiovascular positive',
    aliases: ['ros cvs', 'cardiac ros'],
    category: 'Review of systems',
    sections: ['D'],
    body:
      'CVS: chest pain on exertion as documented above. ' +
      'Palpitations: ___. Orthopnoea / paroxysmal nocturnal dyspnoea: ___. ' +
      'Ankle swelling: ___. ' +
      'Reduced exercise tolerance — used to walk ___ km, now ___. ' +
      'No syncope, no presyncope. ' +
      'Other systems unremarkable.',
  },
  {
    trigger: 'ROS — neurological positive',
    aliases: ['ros neuro', 'neuro ros'],
    category: 'Review of systems',
    sections: ['D'],
    body:
      'CNS: headache as above (location / character / duration). ' +
      'No visual disturbance / diplopia. ' +
      'No focal weakness / numbness / paraesthesia. ' +
      'No speech or swallowing problems. ' +
      'No seizures / blackouts. ' +
      'No memory / mood / sleep disturbance. ' +
      'Gait normal. Other systems unremarkable.',
  },

  // ====================================================================
  // PREGNANCY / WOMEN'S HEALTH
  // Histories go to B; the routine ANC review can also seed N.
  // ====================================================================
  {
    trigger: 'ANC routine review',
    aliases: ['antenatal review', 'anc visit'],
    category: 'Women\'s health',
    sections: ['B', 'N'],
    body:
      'Antenatal review at __ weeks. ' +
      'No vaginal bleeding / abdominal pain / reduced fetal movements / fluid loss. ' +
      'BP, urine dipstick, fundal height, fetal heart rate documented above. ' +
      'Iron + folate continued. Tetanus / malaria prophylaxis up to date per schedule. ' +
      'Counselled on birth-preparedness + danger signs. Next ANC in __ weeks.',
  },
  {
    trigger: 'Postnatal review (6-week)',
    aliases: ['postpartum review', 'pnc'],
    category: 'Women\'s health',
    sections: ['B', 'N'],
    body:
      '6-week postnatal review. Mother + baby well. Lochia settled, no fever / breast issues. ' +
      'Mood + sleep adequate. ' +
      'Contraception discussed and chosen. ' +
      'Baby weight + immunisations up to date.',
  },

  // ====================================================================
  // REFERRAL REASON TEMPLATES — NEW (added 2026-06-04)
  // Relevant to P only. The reason field on the structured Referral
  // row in Section P.
  // ====================================================================
  {
    trigger: 'Specialist opinion — confirmed dx',
    aliases: ['specialist referral', 'specialist opinion'],
    category: 'Referral reasons',
    sections: ['P'],
    body:
      'Specialist opinion sought for management of confirmed ___. ' +
      'Current therapy as documented. Patient remains symptomatic / suboptimally controlled. ' +
      'Would value specialist review with view to ___. ' +
      'Full clinical summary, investigations and current treatment attached.',
  },
  {
    trigger: 'Surgical assessment — elective',
    aliases: ['surgical opinion', 'elective surgery referral'],
    category: 'Referral reasons',
    sections: ['P'],
    body:
      'Surgical assessment for elective ___. ' +
      'Patient counselled regarding diagnosis and the role of surgery. ' +
      'Pre-operative workup attached (FBC, UEC, LFTs, ECG where indicated). ' +
      'No contraindications identified at this consultation. ' +
      'Please advise on listing for theatre.',
  },
  {
    trigger: 'Urgent surgical / acute referral',
    aliases: ['urgent surgical', 'acute abdomen referral'],
    category: 'Referral reasons',
    sections: ['P'],
    body:
      'URGENT referral. Acute ___ requiring same-day surgical assessment. ' +
      'Clinical findings as documented above; relevant imaging / bloods attached. ' +
      'Patient is currently ___ (haemodynamically stable / unstable). ' +
      'IV access established, analgesia given, fasting from ___. ' +
      'Awaiting your team\'s assessment.',
  },
  {
    trigger: 'Financial constraints',
    aliases: ['cannot afford', 'cost referral', 'subsidised care'],
    category: 'Referral reasons',
    sections: ['P'],
    body:
      'Patient unable to meet the out-of-pocket cost of recommended care at this facility. ' +
      'Referring for assessment under the public sector / subsidised programme. ' +
      'Clinical summary attached. ' +
      'Will support transfer with copies of all investigations performed to date.',
  },
  {
    trigger: 'Further imaging / investigations',
    aliases: ['imaging referral', 'investigation referral', 'ct mri'],
    category: 'Referral reasons',
    sections: ['P'],
    body:
      'Imaging / investigations required to confirm or exclude ___ ' +
      'are not available at this facility. ' +
      'Referring for: ___ (CT / MRI / endoscopy / specialised pathology). ' +
      'Provisional diagnosis and current management documented. ' +
      'Please return findings to the referring team.',
  },
  {
    trigger: 'Cardiology review',
    aliases: ['cardiology referral', 'cardiac specialist'],
    category: 'Referral reasons',
    sections: ['P'],
    body:
      'Cardiology opinion sought for ___ ' +
      '(chest pain investigation / arrhythmia / heart failure / valvular disease). ' +
      'ECG, CXR, troponin and basic bloods attached. ' +
      'Patient currently ___ (stable / unstable). ' +
      'Awaiting your team\'s review.',
  },
  {
    trigger: 'Oncology / malignancy workup',
    aliases: ['oncology referral', 'suspected cancer', 'malignancy'],
    category: 'Referral reasons',
    sections: ['P'],
    body:
      'Suspected malignancy: ___ ' +
      '(mass / lymphadenopathy / unexplained weight loss / abnormal imaging finding). ' +
      'Initial investigations attached. ' +
      'Patient counselled regarding the suspicion and the need for further workup ' +
      'including biopsy / staging imaging. ' +
      'Referring for specialist oncology opinion.',
  },
  {
    trigger: 'PMGH transfer — higher-level care',
    aliases: ['pmgh transfer', 'tertiary transfer', 'port moresby gen hospital'],
    category: 'Referral reasons',
    sections: ['P'],
    body:
      'Transfer to Port Moresby General Hospital (PMGH) for higher-level care of ___. ' +
      'Stabilised at this facility: IV access, fluids, analgesia, initial antibiotics / oxygen as documented. ' +
      'Vital signs at transfer: ___. ' +
      'Accompanied by: ___. ' +
      'Receiving team contacted on ___.',
  },

  // ====================================================================
  // PLANS / DISPOSITIONS — Follow-up plan (R)
  // ====================================================================
  {
    trigger: 'Discharge home — stable',
    aliases: ['discharge plan', 'sent home'],
    category: 'Plan & disposition',
    sections: ['R'],
    body:
      'Patient clinically stable for discharge. Medications dispensed and explained. ' +
      'Return immediately if: fever > 39 °C, worsening pain, vomiting unable to keep fluids down, ' +
      'shortness of breath, reduced consciousness. ' +
      'Follow-up review in __ days.',
  },
  {
    trigger: 'Refer to specialist (follow-up)',
    aliases: ['referral plan'],
    category: 'Plan & disposition',
    sections: ['R'],
    body:
      'Clinical findings discussed with patient. ' +
      'Referring to __ specialty for further assessment / management. ' +
      'Awaiting appointment. Continue current symptomatic therapy. ' +
      'Patient understands. Re-attend earlier if symptoms worsen.',
  },
  {
    trigger: 'Admit for observation',
    aliases: ['admit plan', 'admit ward'],
    category: 'Plan & disposition',
    sections: ['N', 'R'],
    body:
      'Admit to general ward for observation + supportive care. ' +
      'Vital signs 4-hourly. IV access × 1. ' +
      'Bloods: FBC, UEC, LFTs, blood culture if febrile. ' +
      'Daily review by ward team. ' +
      'Discharge criteria documented.',
  },
  {
    trigger: 'Discharge with medications',
    aliases: ['discharge meds', 'home with rx'],
    category: 'Plan & disposition',
    sections: ['N'],
    body:
      'Discharge home with the medications dispensed (see Section O). ' +
      'Instructions: take as prescribed; complete the full antibiotic course. ' +
      'Return if symptoms worsen or new symptoms develop. ' +
      'Review in ___ days at this clinic or local health centre.',
  },
  {
    trigger: 'Continue current treatment',
    aliases: ['continue rx', 'no change', 'maintenance review'],
    category: 'Plan & disposition',
    sections: ['N'],
    body:
      'Continue current regimen — no changes today. ' +
      'Patient counselled on adherence and possible side-effects. ' +
      'Review in ___ weeks with: ___. ' +
      'Bring all medications to next visit.',
  },
  {
    trigger: 'Await investigations',
    aliases: ['await results', 'pending workup'],
    category: 'Plan & disposition',
    sections: ['N'],
    body:
      'Continue supportive care. ' +
      'Withhold definitive treatment decision pending: ___ ' +
      '(blood results / cultures / imaging / specialist opinion). ' +
      'Patient counselled on the reason for the wait and the danger signs to escalate. ' +
      'Will review on ___.',
  },
  {
    trigger: 'Escalate / refer if worsening',
    aliases: ['safety net', 'red flags', 'escalation criteria'],
    category: 'Plan & disposition',
    sections: ['N'],
    body:
      'If: worsening pain, persistent fever > 39 °C, vomiting unable to keep oral intake, ' +
      'drowsiness, breathing difficulty, or reduced urine output — escalate immediately. ' +
      'Patient and family briefed. ' +
      'Nearest higher-level facility: ___ (tel: ___).',
  },
]

// Group by category for the picker UI (visual grouping in the popover).
export function groupSnippetsByCategory(snippets = CLINICAL_SNIPPETS) {
  const buckets = new Map()
  for (const s of snippets) {
    if (!buckets.has(s.category)) buckets.set(s.category, [])
    buckets.get(s.category).push(s)
  }
  return Array.from(buckets.entries()).map(([category, items]) => ({ category, items }))
}

// Filter by free-text query against trigger + aliases + category.
export function searchSnippets(query, snippets = CLINICAL_SNIPPETS) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return snippets
  return snippets.filter((s) => {
    const haystack = [
      s.trigger,
      ...(s.aliases || []),
      s.category,
    ].join(' ').toLowerCase()
    return haystack.includes(q)
  })
}
